import React, { FC } from 'react';
import { IG_TIERS, ImplementationGroup } from '@constants/entities/project';
import { Box, Chip, IconButton, Stack } from '@mui/material';
import { themePalette } from '@theme/muiTheme';
import { CheckIcon, SearchIcon } from '@utils/iconsDefs';

/**
 * The four pills in the order the panel shows them: the three tiers, then
 * the Controls overview.
 */
export const CIS_PILLS: ImplementationGroup[] = [
  ...IG_TIERS,
  ImplementationGroup.Controls,
];

export const CIS_SEARCH_LABEL = 'Search references';

type Props = {
  pill: ImplementationGroup;
  onPillChange: (pill: ImplementationGroup) => void;
  onSearch: () => void;
};

/**
 * The IG1 · IG2 · IG3 · Controls pill row of the CIS-18 reference panel,
 * with the search icon at its right (AC1.2.2, AC1.2.3).
 *
 * The pills are navigation, not selection: exactly one is active — the
 * section whose list shows beneath — and it wears the check, so they are
 * exposed as toggle buttons with `aria-pressed` rather than as checkboxes.
 * Presentational: the container owns which pill is active and hands it in,
 * and is told of a click on any pill, the active one included, and on the
 * search icon. Active is the Figma selected pill — the green-550 fill,
 * the green-900 border and a 16px check — drawn at the resting pill's
 * height and padding so the row does not shift. The row is 52px and the
 * icon button 36px, as the design frames them.
 */
const FilterCisPillRow: FC<Props> = ({ pill, onPillChange, onSearch }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap="8px"
    sx={{ height: 52, px: '12px', flexShrink: 0 }}
  >
    {CIS_PILLS.map((option) => {
      const isActive = option === pill;

      return (
        <Chip
          key={option}
          variant="grey-outlined"
          label={option}
          aria-pressed={isActive}
          icon={isActive ? <CheckIcon aria-hidden /> : undefined}
          onClick={() => onPillChange(option)}
          sx={{
            borderRadius: '128px',
            borderColor: isActive
              ? themePalette.green[900]
              : themePalette.grey[700],
            bgcolor: isActive ? themePalette.green[550] : 'transparent',
            '&:hover': {
              bgcolor: isActive
                ? themePalette.green[600]
                : themePalette.grey[500],
            },
            '& .MuiChip-icon': {
              width: 16,
              height: 16,
              fill: themePalette.primary.main,
            },
          }}
        />
      );
    })}

    <Box flex={1} />

    <IconButton
      size="small"
      variant="simple"
      aria-label={CIS_SEARCH_LABEL}
      onClick={onSearch}
      sx={{
        width: 36,
        height: 36,
        borderRadius: '4px',
        border: 'none',
        flexShrink: 0,
      }}
    >
      <SearchIcon width={20} height={20} />
    </IconButton>
  </Stack>
);

export default FilterCisPillRow;
